import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { format } from 'date-fns';
import { Calendar, Tag, Bookmark, ArrowLeft } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { renderMarkdown } from '../lib/markdoc';
import { getBlogPostCache, setBlogPostCache } from '../lib/cache';
import type { BlogPost } from '../types/blog';

export function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchPost() {
      if (!slug) return;

      try {
        // Check cache first
        const cachedPost = getBlogPostCache(slug);
        if (cachedPost) {
          setPost(cachedPost);
          setLoading(false);
          
          // Fetch in background to update cache
          fetchFromApi();
          return;
        }

        await fetchFromApi();
      } catch (err) {
        console.error('Error fetching post:', err);
        setError('Failed to load blog post');
        setLoading(false);
      }
    }

    async function fetchFromApi() {
      if (!slug) return;

      const { data, error } = await supabase
        .from('blog_posts')
        .select(`
          *,
          category:blog_categories(name, slug)
        `)
        .eq('slug', slug)
        .eq('status', 'published')
        .is('deleted_at', null)
        .single();

      if (error) throw error;
      
      setPost(data);
      setBlogPostCache(slug, data);
      setLoading(false);
    }

    fetchPost();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[--bg-secondary] py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse space-y-8">
            <div className="h-8 bg-[--bg-tertiary] rounded w-3/4"></div>
            <div className="space-y-4">
              <div className="h-4 bg-[--bg-tertiary] rounded w-1/2"></div>
              <div className="h-4 bg-[--bg-tertiary] rounded w-full"></div>
              <div className="h-4 bg-[--bg-tertiary] rounded w-3/4"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-[--bg-secondary] py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-error-50 dark:bg-[rgb(var(--error-900)/0.2)] p-4 rounded-lg">
            <p className="text-error-600">
              {error || 'Blog post not found'}
            </p>
            <Link
              to="/blog"
              className="inline-flex items-center mt-4 text-error-600 hover:text-error-800"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to blog
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[--bg-secondary] py-12">
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link
          to="/blog"
          className="inline-flex items-center text-[--text-secondary] hover:text-[--text-primary] mb-8"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to blog
        </Link>

        {post.featured_image && (
          <img
            src={post.featured_image}
            alt={post.title}
            className="w-full h-64 md:h-96 object-cover rounded-lg mb-8"
          />
        )}

        <div className="bg-[--bg-primary] shadow-lg rounded-lg overflow-hidden">
          <div className="p-8">
            <div className="flex flex-wrap items-center gap-4 text-sm text-[--text-secondary] mb-6">
              <div className="flex items-center">
                <Calendar className="w-4 h-4 mr-1" />
                {format(new Date(post.published_at || post.created_at), 'MMMM d, yyyy')}
              </div>
              {post.category && (
                <div className="flex items-center">
                  <Bookmark className="w-4 h-4 mr-1" />
                  {post.category.name}
                </div>
              )}
              <div className="flex items-center">
                <span className="font-medium text-[--text-primary]">
                  {post.author_name}
                </span>
              </div>
            </div>

            <h1 className="text-3xl md:text-4xl font-bold text-[--text-primary] mb-6">
              {post.title}
            </h1>

            {post.summary && (
              <p className="text-xl text-[--text-secondary] mb-8">
                {post.summary}
              </p>
            )}

            <div className="prose dark:prose-invert max-w-none">
              {renderMarkdown(post.content)}
            </div>
          </div>
        </div>
      </article>
    </div>
  );
}